import type { AdminAuditLogResponse } from '@inflip/contracts/admin';
import { useCallback, useEffect, useState } from 'react';

import { Button, EmptyState, ErrorBanner, PageHeader, inputCls } from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { formatDateTime } from '../lib/time-zone';
import { useListViewState } from '../lib/use-list-view-state';

export function AuditLogsPage() {
  const { session } = useAdminAuth();
  const [search, setSearch] = useListViewState('q', '');
  const [q, setQ] = useState(search);
  const [page, setPage] = useListViewState('page', 1);
  const [rows, setRows] = useState<AdminAuditLogResponse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!session) return;
    api
      .listAuditLogs(session.accessToken, { page, ...(search ? { q: search } : {}) })
      .then(setRows)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [session, search, page]);

  useEffect(load, [load]);

  if (!session) return null;

  return (
    <main className="w-full px-6 py-8">
      <PageHeader
        title="감사 로그"
        description="어드민 액션 기록을 검색하고 상세를 확인해요"
        actions={
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setSearch(q);
            }}
          >
            <input
              className={`w-64 ${inputCls}`}
              placeholder="요청 주소 또는 대상 id 검색 (예: confirm-payment)"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Button type="submit">검색</Button>
          </form>
        }
      />
      {error && <ErrorBanner message={error} />}

      <div className="-mx-3 divide-y divide-border/60">
        {rows.map((log) => (
          <div key={log.id} className="text-sm">
            <button
              type="button"
              className="flex w-full items-center gap-3 rounded-md px-3 py-3 text-left transition hover:bg-muted"
              onClick={() => setExpanded(expanded === log.id ? null : log.id)}
            >
              <span className="w-36 shrink-0 text-xs tabular-nums text-muted-foreground">
                {formatDateTime(log.createdAt)}
              </span>
              <span className="min-w-0 flex-1 truncate font-mono text-xs">{log.action}</span>
              <span
                className={`shrink-0 text-xs font-bold ${
                  log.actorRole === 'SUPER_ADMIN' ? 'text-brand' : 'text-muted-foreground'
                }`}
              >
                {log.actorRole}
              </span>
            </button>
            {expanded === log.id && (
              <div className="mx-3 mb-3 rounded-md bg-muted/60 px-4 py-3 text-xs">
                <div>
                  actor: <span className="font-mono">{log.actorId}</span>
                </div>
                <div>
                  target: {log.targetType}{' '}
                  <span className="font-mono">{log.targetId ?? '—'}</span>
                </div>
                <div>ip: {log.ip ?? '—'}</div>
                {log.payload && (
                  <pre className="mt-1 overflow-x-auto rounded-md bg-background p-2">
                    {JSON.stringify(log.payload, null, 2)}
                  </pre>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {rows.length === 0 && (
        <div className="mt-8">
          <EmptyState message="로그가 없어요." />
        </div>
      )}

      <div className="mt-6 flex items-center justify-center gap-4 text-sm">
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 1}
          onClick={() => setPage((p) => p - 1)}
        >
          ← 이전
        </Button>
        <span className="tabular-nums text-muted-foreground">{page} 페이지</span>
        <Button
          variant="outline"
          size="sm"
          disabled={rows.length < 20}
          onClick={() => setPage((p) => p + 1)}
        >
          다음 →
        </Button>
      </div>
    </main>
  );
}
